import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Rocket, HandCoins, TrendingUp, ArrowRight, Sparkles } from "lucide-react";
import PublicNavbar from "../components/layout/PublicNavbar";
import Seo from "../components/Seo";

/**
 * AuthChooser — rendered at `/login` and `/signup`.
 *
 * Layout mirrors the homepage IntentPicker: two "VenturaZ" cards
 * (Equity + Invest) side by side in a top row, followed by a wider
 * distinct **VenturaZ Money · Debt Ecosystem** card so borrowers
 * immediately understand it's a separate sister-product.
 */

function VenturaZCard({ c, onClick, isLogin }) {
  const Icon = c.icon;
  const accentBar = c.accent === "gold" ? "bg-gold" : "bg-navy";
  const iconBg = c.accent === "gold" ? "bg-gold text-navy" : "bg-navy text-cream";
  const chip = c.accent === "gold" ? "text-[#8A6600] border-gold/40" : "text-navy border-navy/30";
  return (
    <button
      type="button"
      onClick={onClick}
      className="group text-left liquid-morph-panel rounded-2xl p-6 text-ink hover:-translate-y-1 transition-transform focus:outline-none focus:ring-2 focus:ring-gold"
      data-testid={c.testId}
    >
      <div className={`h-1 w-12 rounded-full ${accentBar} mb-5`} />
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-11 h-11 rounded-lg inline-flex items-center justify-center ${iconBg}`}>
          <Icon size={20} />
        </div>
        <div>
          <div className={`inline-block text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${chip} mb-1`}>{c.tag}</div>
          <div className="font-heading text-ink text-lg leading-tight">{c.title}</div>
        </div>
      </div>
      <div className="text-[13px] text-ink-soft leading-relaxed mb-4">{c.body}</div>
      <div className="inline-flex items-center gap-1 text-sm font-heading text-[#8A6600] group-hover:underline">
        {isLogin ? "Sign in" : "Create account"} <ArrowRight size={14} />
      </div>
    </button>
  );
}

function DebtCard({ isLogin, nav }) {
  return (
    <button
      type="button"
      onClick={() => nav(isLogin ? "/money/login" : "/money/signup")}
      className="group relative overflow-hidden rounded-2xl border border-[#B8860B]/30 hover:-translate-y-1 transition-all text-left focus:outline-none focus:ring-2 focus:ring-gold"
      style={{ background: "linear-gradient(135deg, #1F1408 0%, #3E2410 45%, #7A4E1D 100%)" }}
      data-testid="auth-choose-debt"
    >
      <div className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full opacity-40" style={{ background: "radial-gradient(circle, #E9C46A 0%, transparent 60%)" }} />
      <div className="pointer-events-none absolute -bottom-32 -left-24 w-96 h-96 rounded-full opacity-20" style={{ background: "radial-gradient(circle, #B8860B 0%, transparent 60%)" }} />
      <div className="relative p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-2 mb-4 flex-wrap">
            <span className="inline-flex items-center gap-1.5 bg-gold text-[#1F1408] text-[10px] font-mono uppercase tracking-[0.24em] font-bold px-2.5 py-1 rounded-full shadow-[0_4px_16px_rgba(233,196,106,0.35)]" data-testid="auth-choose-debt-chip">
              <Sparkles size={11} /> VenturaZ MONEY · Debt Ecosystem
            </span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#E9C46A]/70">A different world · Same account</span>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg inline-flex items-center justify-center bg-gold text-[#1F1408] shadow-[0_8px_24px_rgba(233,196,106,0.35)] shrink-0">
              <HandCoins size={22} />
            </div>
            <div className="min-w-0">
              <div className="font-display text-[#FFF3D9] text-2xl md:text-3xl leading-tight mb-2">
                Access VenturaZ Money
              </div>
              <div className="text-[#FFF3D9]/80 text-[13px] md:text-sm leading-relaxed max-w-2xl">
                India&apos;s honest loan advisor. Compare offers from 85+ banks & NBFCs, free CIBIL worth ₹999, zero platform fee. Advisor, never lender.
              </div>
            </div>
          </div>
        </div>
        <div className="shrink-0 md:w-52 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-gold text-[#1F1408] text-sm font-heading font-bold group-hover:bg-yellow-400 transition-colors shadow-[0_8px_24px_rgba(233,196,106,0.28)]">
          {isLogin ? "Sign in" : "Create account"} <ArrowRight size={14} />
        </div>
      </div>
    </button>
  );
}

export default function AuthChooser({ mode = "login" }) {
  const nav = useNavigate();
  const isLogin = mode === "login";
  const seoTitle = isLogin ? "Sign In · VenturaZ" : "Join VenturaZ";
  const heading = isLogin ? "Welcome back — which surface are you on today?" : "Which VenturaZ are you here for?";

  const cards = [
    {
      key: "equity",
      title: "Raise Equity",
      tag: "VENTURAZ · FOR FOUNDERS",
      body: "Get AI-matched to family offices, funds, and strategics. Trust-scored profiles, verified data rooms, native e-sign.",
      icon: Rocket,
      accent: "navy",
      to: isLogin ? "/for-startups/login" : "/for-startups/signup",
      testId: "auth-choose-equity",
    },
    {
      key: "invest",
      title: "Invest in Startups",
      tag: "VENTURAZ · FOR INVESTORS",
      body: "Access hand-picked deal flow across sectors. VenturaZ Handpicked ribbon on curated deals. Deal rooms + DD access.",
      icon: TrendingUp,
      accent: "gold",
      to: isLogin ? "/for-investors/login" : "/for-investors/signup",
      testId: "auth-choose-investor",
    },
  ];

  return (
    <div className="min-h-screen text-cream relative liquid-morph-hero !border-0 !shadow-none !rounded-none">
      <Seo title={seoTitle} description="Pick your entry into VenturaZ — raise equity, raise debt, or invest." path={isLogin ? "/login" : "/signup"} noindex />
      <PublicNavbar />
      <section className="relative max-w-6xl mx-auto px-6 py-16 lg:py-24">
        <div className="overline mb-3 text-gold/90">{isLogin ? "SECURE LOGIN" : "JOIN VENTURAZ"}</div>
        <h1 className="font-display text-3xl md:text-4xl lg:text-5xl text-cream mb-3 max-w-3xl leading-tight" data-testid="auth-chooser-heading">
          {heading}
        </h1>
        <p className="text-cream/70 text-base mb-10 max-w-2xl">
          Pick your surface so we route you to the right dashboard{isLogin ? "" : " — you can join a second one later"}.
        </p>

        {/* Row 1 — VenturaZ pair */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5" data-testid="auth-chooser-venturaz-row">
          {cards.map((c) => (
            <VenturaZCard key={c.key} c={c} isLogin={isLogin} onClick={() => nav(c.to)} />
          ))}
        </div>

        {/* Row 2 — VenturaZ Money · Debt Ecosystem */}
        <DebtCard isLogin={isLogin} nav={nav} />

        <div className="mt-8 text-sm text-cream/80 text-center flex flex-wrap items-center justify-center gap-2">
          {isLogin ? (
            <>
              <span>New to VenturaZ?</span>
              <Link to="/signup" className="text-gold font-semibold hover:underline" data-testid="auth-chooser-signup-link">Create an account →</Link>
              <span className="text-cream/40">·</span>
              <Link to="/admin/login" className="hover:text-gold">Admin login</Link>
            </>
          ) : (
            <>
              <span>Already have an account?</span>
              <Link to="/login" className="text-gold font-semibold hover:underline" data-testid="auth-chooser-login-link">Sign in →</Link>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
